import { Grid, Card, CardHeader, CardContent, Button, Typography, Checkbox, Tooltip } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { Service } from '../../../models/masters/service';
import { useEffect, useState } from 'react';
import { SelectServicesDialog } from '../../services/select-services.dialog';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { servicesColumns } from '../../services/grid-columns';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { FormGridEditable } from '../../../components/hook-form/form-grid-editable.component';
import { servicesEditableColumns } from '../../services/grid-editable-columns';
import { ApiResponse } from '../../../models/api-response';
import { useLoadData } from '../../../hooks/useLoadData';
import { GridCellProps } from '@progress/kendo-react-grid';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useAuthorize } from '../../../hooks/use-authorize';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { GuardedComponent } from '../../../components/guarded-component';
import ServiceNotReadyFields from '../../services/service-not-ready-fields';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';
import { OfficeServiceNewPriceFields } from './service-new-price-fields.component';
import OfficeServiceNotReadyFields from './office-service-not-ready-fields.component';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
  headingStyle: { fontWeight: 'bold' },
};
const SELECTED_FIELD = 'isSelected';
interface ServicesFormProps {
  name: string;
  page?: string;
  id?: number;
  officeServices?: any;
  refetchPricing?: boolean;
  onOpenNewPriceDialog: () => void;
  openEditPriceDialog: (pricingID: any) => void;
  updatedOldServices: (services: any) => void;
}

const DEFAULT_GROUP = [
  {
    field: 'serviceGroupName',
  },
];

export const ServicesForm = ({ name, page, id, officeServices, refetchPricing, onOpenNewPriceDialog, openEditPriceDialog, updatedOldServices }: ServicesFormProps) => {
  const { setValue, getValues, watch } = useFormContext();
  const [openDialog, setOpenDialog] = useState(false);
  const [updatedService, setUpdatedService] = useState<Service[]>([]);
  const isDisableEditableColumn = useAuthorize([UserPermissionName.EDIT_SREVICE_PRICE_OFFICE]);
  const isDisableCheckBoxBasedOnPermission = useAuthorize([UserPermissionName.DELETE_SERVICE_OFFICE]);
  watch(['office.customerID']);

  const { data, isLoading } = useLoadData<ApiResponse<Service[]>>(
    servicesApiKeyStore.getServiceCustomerMappings(getValues('customer.customerID'))
  );

  useWatch({
    name: ['customer.customerID'] || [],
  });

  const [initialService, setInitilalService] = useState<Service[]>(watch(name));
  const [disabledPricing, setDisabledPricing] = useState<boolean>(false);

  const customColumns: GridColumnConfig[] = [
    {
      title: 'Invoice Amount ($)', // Main heading
      field: '', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      children: [
        {
          title: 'Service',
          field: 'price',
          filter: 'text',
          fieldType: 'currency',
          editable: !disabledPricing,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
        {
          title: 'Retest',
          field: 'invoiceRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: !disabledPricing,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
      ],
    },
    {
      title: 'Accrual Amount ($)', // Main heading for accrual
      field: '', // Leave empty as it is a group header
      filter: 'text',
      isHideColumnMenu: true,
      children: [
        {
          title: 'Service',
          field: 'accrualAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: !disabledPricing,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
        {
          title: 'Retest',
          field: 'accrualRetestAmount',
          filter: 'text',
          fieldType: 'currency',
          editable: !disabledPricing,
          editor: 'numeric',
          isDecimal: true,
          isDisable: !isDisableEditableColumn,
        },
      ],
    },
  ];

  const servicesEditableColumnsWithPermission: GridColumnConfig[] = [...servicesEditableColumns, ...customColumns];

  useEffect(() => {
    if (data) {
      if (!id) {
        setValue(name, data.data);
        setUpdatedService(data.data);
      }
    }
  }, [data]);

  useEffect(() => {
    // Listen for changes in the form state after calling setValue
    if (!id) {
      setValue(name, updatedService);
    }
  }, [updatedService]);

  // useEffect(() => {
  //   if (getValues(name)) {

  //     showPlan(getValues(name).some((item: any) => item.isPlanReviewRequired));
  //   }
  // }, [getValues(name)]);

  const handleOpenSelectServiceDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseSelectServiceDialog = (services: Service[]) => {
    setOpenDialog(false);

    if (selectedPricingID) {
      const updatedServiceData = services?.map(item1 => {
        const matchedItem = initialService?.find(
          item2 => item2.serviceID === item1.serviceID
        );
        return matchedItem ? { ...item1, ...matchedItem } : item1;
      });
      setInitilalService(updatedServiceData);
      setValue(name, updatedServiceData);
    }
    else {
      setInitilalService(services);
      setValue(name, services);
    }
    updatedOldServices(services);
  };

  const conditionalCellRender = (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (props.field === SELECTED_FIELD && props.dataItem?.isBundled) {
      return (
        <Tooltip title={<Typography variant="body1">Already used in bundle creation</Typography>}>
          <td className="chkInputCust">
            <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.isBundled} />
          </td>
        </Tooltip>
      );
    } else if (props.field === SELECTED_FIELD && props.dataItem?.isRetestService) {
      return (
        <td>
          <Checkbox checked={props.dataItem?.isSelected} disabled={props.dataItem?.isRetestService} />
        </td>
      );
    } else if (
      props.field === SELECTED_FIELD &&
      props.dataItem?.mappingID !== 0 &&
      !isDisableCheckBoxBasedOnPermission
    ) {
      return (
        <Tooltip title={<Typography variant="body1">You do not have permission for delete</Typography>}>
          <td>
            <Checkbox checked={props.dataItem?.isSelected} disabled={true} />
          </td>
        </Tooltip>
      );
    } else {
      return defaultRendering;
    }
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout(!page ? 'unKnown' : page, name));

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredService, setFilteredService] = useState<Service[]>(watch(name));

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const services = watch(name);

  useEffect(() => {
    const filteredServiceList = initialService
      ?.filter((d: Service) => !d.isNotReadyService)
      .filter((service: Service) =>
        Object.values(service).some(
          (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
      );
    setFilteredService(filteredServiceList || []);
  }, [debouncedSearchTerm, initialService]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: name,
      pageName: !page ? 'unKnown' : page,
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: name,
      pageName: !page ? 'unKnown' : page,
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  const areArraysEqual = (arr1: Service[], arr2: Service[]) => {
    return arr1.every((item, index) => item?.serviceID === arr2[index]?.serviceID);
  };

  useEffect(() => {
    if (!services || services?.length === 0) {
      return;
    }
    if (!areArraysEqual(services, filteredService)) {
      setFilteredService(services?.filter((d: Service) => !d.isNotReadyService));
    }
  }, [services]);

  const [selectedPricingID, setSelectedPricingID] = useState<any>(0);

  const { data: pricingListData, isLoading: isPricingLoading, isFetching: isPricingFetching, refetch } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.getProjectPricingDetailsByOfficeID(getValues('officeID'), selectedPricingID)
  );

  useEffect(() => { refetch(); }, [refetchPricing])

  const getServicePriceByName = (serviceData: any, serviceName: any) => {
    const service = serviceData?.find(
      (item: any) => item.serviceName === serviceName
    );
    return service ? service.price : null;
  };

  useEffect(() => {
    if (pricingListData) {
      const updatedServices = filteredService?.map(service => {
        const newService = pricingListData?.data?.services?.find((newService: any) => newService.serviceID === service.serviceID);
        if (newService) {
          return {
            ...service, // Spread the existing service properties
            price: newService.price ?? "",
            accrualAmount: newService.accrualAmount ?? "",
            invoiceRetestAmount: newService.invoiceRetestAmount ?? "",
            accrualRetestAmount: newService.accrualRetestAmount ?? ""
          };
        }
        return service;
      });

      setValue("dryRunFee", getServicePriceByName(pricingListData?.data?.services, "Dry Run"));
      setValue("lateCancellationFee", getServicePriceByName(pricingListData?.data?.services, "Late Cancellation"));
      setValue("tripCharge", getServicePriceByName(pricingListData?.data?.services, "Trip Charge"));

      const filteredServiceList = updatedServices
        ?.filter((d: Service) => !d.isNotReadyService);

      setInitilalService(filteredServiceList);
      setValue(name, filteredServiceList);
    }

  }, [pricingListData])

  const handlePricingSelect = (priceData: any) => {
    if (priceData) {
      setSelectedPricingID(priceData?.pricingListID);
      setDisabledPricing(true);
    }
    else {

      const filteredServiceList = officeServices
        ?.filter((d: Service) => !d.isNotReadyService);
      setSelectedPricingID(0);
      setDisabledPricing(false);
      setInitilalService(filteredServiceList)
      setValue(name, filteredServiceList);
      setValue("dryRunFee", getServicePriceByName(officeServices, "Dry Run"));
      setValue("lateCancellationFee", getServicePriceByName(officeServices, "Late Cancellation"));
      setValue("tripCharge", getServicePriceByName(officeServices, "Trip Charge"));
    }
  }

  return (
    <Card elevation={0}>
      {/* <ServiceNotReadyFields /> */}
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            SERVICES AND PRICING
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.ADD_SERVICE_OFFICE]}>
            <Button
              className="btnLink"
              variant="text"
              sx={{ p: 0, m: 0, mr: 1 }}
              onClick={handleOpenSelectServiceDialog}
            >
              Add New Service
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent>
        <Grid container spacing={2}>
          {id != 0 && (
            <OfficeServiceNewPriceFields
              officeID={getValues('officeID')}
              refetchPricing={refetchPricing}
              onPriceSelect={handlePricingSelect}
              openNewPriceDialog={onOpenNewPriceDialog}
              openEditPriceDialog={openEditPriceDialog} />
          )}
          <OfficeServiceNotReadyFields disabledPricing={disabledPricing} isParentLoading={isPricingLoading || isPricingFetching} />
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <FormGridEditable
              fileTitle='office_service'
              dataItemKey="serviceID"
              // defaultGroup={DEFAULT_GROUP}
              columns={servicesEditableColumnsWithPermission}
              name={name}
              isBusy={isLoading || isPricingLoading || isPricingFetching}
              // dataItems={getValues(name)?.filter((d: Service) => !d.isNotReadyService)}

              dataItems={filteredService || []}
              reorderable
              handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
        </Grid>
        {openDialog && (
          <SelectServicesDialog
            open={openDialog}
            isNotReadyService
            handleCancelClick={() => setOpenDialog(false)}
            conditionalCellRender={conditionalCellRender}
            preventCheckAndUnCheckOnHeaderelectionChange={['isBundled', 'isRetestService']}
            isShowUncheckConfirmation={true}
            selectedField="isSelected"
            // selectedField={isDisableCheckBoxBasedOnPermission ? 'isSelected' : undefined}
            // servicesApiQuery={() => servicesApiKeyStore.getServiceCustomerMappings(getValues('customer.customerID'))}
            onClose={handleCloseSelectServiceDialog}
            services={getValues(name) ?? []}
            servicesApiQuery={() =>
              servicesApiKeyStore.getServiceMappingList(
                !id ? getValues('customer.customerID') : 0,
                !id ? 0 : id,
                0,
                0,
                0
              )
            }
          />
        )}
      </CardContent>
    </Card>
  );
};
